import { useCallback, useEffect, useRef, useState } from "react";
import amsService from "../../../../../../../../../Service/OPSServices/StoreHealthServices/amsService.js";

const POLL_MS = 3000;
const MAX_POLLS = 400;                        // about 20 minutes, the task's own time limit

/**
 * Starts a button's Celery task and polls /ams/tasks/<id> until it finishes.
 * onFinish(action, { ok, result, error, stillRunning }) is called once per run.
 * Runs already in progress (e.g. started in another tab) are picked up from `actions`.
 */
export default function useActionRunner(actions, onFinish) {
  const [busy, setBusy] = useState({});      // { "confirm-traffic": true, ... }
  const timers = useRef({});
  const polling = useRef({});
  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;

  const stop = (action) => {
    clearTimeout(timers.current[action]);
    delete timers.current[action];
    polling.current[action] = false;
    setBusy((b) => ({ ...b, [action]: false }));
  };

  const poll = useCallback((action, taskId) => {
    if (polling.current[action]) return;
    polling.current[action] = true;
    setBusy((b) => ({ ...b, [action]: true }));
    let tries = 0;

    const tick = async () => {
      try {
        const t = await amsService.getTask(taskId);
        if (!t.done && tries++ < MAX_POLLS) {
          timers.current[action] = setTimeout(tick, POLL_MS);
          return;
        }
        stop(action);
        if (t.state === "SUCCESS") finishRef.current(action, { ok: true, result: t.result });
        else if (t.state === "FAILURE") finishRef.current(action, { ok: false, error: t.error || "see Celery results" });
        else finishRef.current(action, { ok: false, stillRunning: true });
      } catch (e) {
        if (tries++ < MAX_POLLS) {
          timers.current[action] = setTimeout(tick, POLL_MS * 2);
          return;
        }
        stop(action);
        finishRef.current(action, { ok: false, error: e.message });
      }
    };
    tick();
  }, []);

  const run = useCallback(
    async (action) => {
      setBusy((b) => ({ ...b, [action]: true }));
      try {
        const { status, data } = await amsService.runAction(action);
        poll(action, data.task_id);
        return { alreadyRunning: status === 409 };
      } catch (e) {
        setBusy((b) => ({ ...b, [action]: false }));
        finishRef.current(action, { ok: false, error: e.message, notStarted: true });
        return { alreadyRunning: false };
      }
    },
    [poll]
  );

  // resume runs that were already in progress when the page loaded
  useEffect(() => {
    (actions || []).forEach((a) => {
      if (a.running_task_id && !polling.current[a.action]) poll(a.action, a.running_task_id);
    });
  }, [actions, poll]);

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  return { busy, run };
}
